import {
  IdentityBrowserContractError,
  decodeAccountFlow,
  decodeLogoutFlow,
  readBoundedJson,
} from "./kratos-browser-contract.js";
import { decodeActiveSession, decodeSessionAlreadyAvailable, decodeSessionError } from "./kratos-session-contract.js";
import {
  FLOW_ID_PATTERN,
  isRecord,
  requiredString,
  validateExactKeys,
} from "./kratos-contract-primitives.js";
import { requiredLegalPublication, LEGAL_ACCEPTANCE_FIELDS, prepareRegistrationLegalAcceptance } from "./legal-acceptance-contract.js";
import { createLegalAcceptanceRepository } from "./legal-acceptance-repository.js";
import { decodeAccountMutationAuthorizationV1, readBoundedAccountJson } from "./account-state-session-repository.js";

const FLOW_VIEWS = new Set([
  "login",
  "registration",
  "settings",
  "recovery",
  "verification",
]);
const DISCORD_AUTHORIZATION_ORIGIN = "https://discord.com";
const DISCORD_AUTHORIZATION_PATH = "/api/v9/oauth2/authorize";
const DISCORD_AUTHORIZATION_QUERY = new Set([
  "client_id",
  "prompt",
  "redirect_uri",
  "response_type",
  "scope",
  "state",
]);
const LOCATION_CHANGE_FIELDS = new Set(["error", "redirect_browser_to"]);
const LOCATION_CHANGE_ERROR_FIELDS = new Set([
  "id",
  "code",
  "status",
  "reason",
  "message",
]);

export class KratosBrowserClientError extends Error {
  constructor(message, { kind = "transport", status = 0 } = {}) {
    super(message);
    this.name = "KratosBrowserClientError";
    this.kind = kind;
    this.status = status;
  }
}

function startUrl(context, view, mode = "default") {
  if (view === "login" && mode === "refresh") return context.refreshLoginStartUrl;
  if (view === "login" && mode === "aal2") return context.stepUpLoginStartUrl;
  if (mode !== "default") {
    throw new TypeError("Identity flow start mode is unsupported.");
  }
  const urls = {
    login: context.loginStartUrl,
    registration: context.registrationStartUrl,
    settings: context.settingsStartUrl,
    recovery: context.recoveryStartUrl,
    verification: context.verificationStartUrl,
  };
  if (!FLOW_VIEWS.has(view) || typeof urls[view] !== "string") {
    throw new TypeError("Identity flow view is unsupported.");
  }
  return urls[view];
}

function flowRoute(value, view) {
  const id = typeof value?.id === "string" ? value.id : "";
  return Object.freeze({ view, flowId: id });
}

function decodeFlow(value, context, view) {
  return decodeAccountFlow(value, context, flowRoute(value, view));
}

function decodeSuccessfulSession(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new KratosBrowserClientError("Identity success response is invalid.", {
      kind: "contract",
    });
  }
  return decodeActiveSession(value.session);
}

function decodeBrowserLocationChange(value, context) {
  if (
    !value
    || typeof value !== "object"
    || Array.isArray(value)
    || value.error?.id !== "browser_location_change_required"
    || typeof value.redirect_browser_to !== "string"
  ) {
    throw new KratosBrowserClientError(
      "Identity continuation response is invalid.",
      { kind: "contract", status: 422 },
    );
  }
  let redirectUrl;
  try {
    redirectUrl = new URL(value.redirect_browser_to);
  } catch (_error) {
    throw new KratosBrowserClientError(
      "Identity continuation URL is invalid.",
      { kind: "contract", status: 422 },
    );
  }
  const identity = redirectUrl.origin === context.apiOrigin
    && redirectUrl.pathname.startsWith("/identity/self-service/")
    && !redirectUrl.username
    && !redirectUrl.password
    && !redirectUrl.hash;
  const continuationEntries = [...redirectUrl.searchParams.entries()];
  const flowContinuation = redirectUrl.origin === context.pageOrigin
    && redirectUrl.pathname === "/account/identity-complete/"
    && !redirectUrl.username
    && !redirectUrl.password
    && !redirectUrl.hash
    && continuationEntries.length === 2
    && FLOW_VIEWS.has(redirectUrl.searchParams.get("view"))
    && FLOW_ID_PATTERN.test(redirectUrl.searchParams.get("flow") ?? "");
  if (!identity && !flowContinuation) {
    throw new KratosBrowserClientError(
      "Identity continuation leaves the approved authentication boundary.",
      { kind: "contract", status: 422 },
    );
  }
  return Object.freeze({
    kind: flowContinuation ? "flowContinuation" : "identityContinuation",
    redirectUrl: redirectUrl.toString(),
    ...(flowContinuation ? {
      view: redirectUrl.searchParams.get("view"),
      flowId: redirectUrl.searchParams.get("flow"),
    } : {}),
  });
}

export function decodeDiscordLocationChange(value, context) {
  try {
    validateExactKeys(value, LOCATION_CHANGE_FIELDS, "Discord continuation");
    validateExactKeys(
      value.error,
      LOCATION_CHANGE_ERROR_FIELDS,
      "Discord continuation error",
    );
  } catch (_error) {
    throw new KratosBrowserClientError(
      "Discord continuation response is invalid.",
      { kind: "contract", status: 422 },
    );
  }
  if (
    value.error.id !== "browser_location_change_required"
    || value.error.code !== 422
    || value.error.status !== "Unprocessable Entity"
    || typeof value.error.reason !== "string"
    || value.error.reason.length === 0
    || value.error.reason.length > 4096
    || typeof value.error.message !== "string"
    || value.error.message.length === 0
    || value.error.message.length > 256
  ) {
    throw new KratosBrowserClientError(
      "Discord continuation response is invalid.",
      { kind: "contract", status: 422 },
    );
  }
  return decodeDiscordAuthorizationUrl(value.redirect_browser_to, context);
}

export function decodeDiscordAuthorizationUrl(value, context) {
  let redirectUrl;
  try {
    redirectUrl = new URL(requiredString(
      value,
      "Discord continuation URL",
      { maxLength: 4096 },
    ));
  } catch (_error) {
    throw new KratosBrowserClientError(
      "Discord continuation URL is invalid.",
      { kind: "contract", status: 422 },
    );
  }
  const entries = [...redirectUrl.searchParams.entries()];
  const queryKeys = new Set(entries.map(([name]) => name));
  const prompt = redirectUrl.searchParams.get("prompt");
  const state = redirectUrl.searchParams.get("state") ?? "";
  const clientId = redirectUrl.searchParams.get("client_id") ?? "";
  if (
    redirectUrl.origin !== DISCORD_AUTHORIZATION_ORIGIN
    || redirectUrl.pathname !== DISCORD_AUTHORIZATION_PATH
    || redirectUrl.username
    || redirectUrl.password
    || redirectUrl.hash
    || entries.length !== DISCORD_AUTHORIZATION_QUERY.size
    || queryKeys.size !== DISCORD_AUTHORIZATION_QUERY.size
    || [...queryKeys].some((name) => !DISCORD_AUTHORIZATION_QUERY.has(name))
    || !/^\d{17,32}$/.test(clientId)
    || !["none", "consent"].includes(prompt)
    || redirectUrl.searchParams.get("redirect_uri")
      !== `${context.identityBaseUrl}/self-service/methods/oidc/callback/discord`
    || redirectUrl.searchParams.get("response_type") !== "code"
    || redirectUrl.searchParams.get("scope") !== "identify"
    || !/^[0-9a-f]{64,512}$/.test(state)
  ) {
    throw new KratosBrowserClientError(
      "Discord continuation leaves the approved provider boundary.",
      { kind: "contract", status: 422 },
    );
  }
  return Object.freeze({
    kind: "providerNavigation",
    redirectUrl: redirectUrl.toString(),
  });
}

function formBody(value) {
  if (typeof FormData !== "undefined" && value instanceof FormData) return value;
  if (value instanceof URLSearchParams) return value;
  throw new TypeError("Identity flow submission requires FormData or URLSearchParams.");
}

export function createKratosBrowserClient({
  context,
  fetchImpl = globalThis.fetch,
  legalPublication = requiredLegalPublication(),
} = {}) {
  if (!context || typeof context !== "object" || typeof fetchImpl !== "function") {
    throw new TypeError("Identity browser client requires a context and fetch implementation.");
  }

  async function submissionBody(flow, submission, signal) {
    const source = formBody(submission);
    if (flow.view !== "registration") return source;
    const body = prepareRegistrationLegalAcceptance(source, legalPublication);
    await createLegalAcceptanceRepository({
      resolveApiUrl: (path) => new URL(path, context.apiOrigin).href,
      fetchImpl, publication: legalPublication,
    }).documents(source.get(LEGAL_ACCEPTANCE_FIELDS.locale), { signal });
    return body;
  }

  async function fetchJson(url, { method = "GET", body, signal, headers = {} } = {}) {
    return fetchImpl(url, {
      method,
      ...(body === undefined ? {} : { body }),
      cache: "no-store",
      credentials: "include",
      headers: { Accept: "application/json", ...headers },
      redirect: "error",
      signal,
    });
  }

  async function readFlow(view, flowId, label, signal) {
    const response = await fetchJson(
      `${context.identityBaseUrl}/self-service/${view}/flows?${new URLSearchParams({
        id: flowId,
      })}`,
      { signal },
    );
    if (!response.ok) {
      throw new KratosBrowserClientError(
        `${label} returned HTTP ${response.status}.`,
        { status: response.status },
      );
    }
    return decodeFlow(await readBoundedJson(response, label), context, view);
  }

  async function resolveFlow(flow, label, signal) {
    if (flow.completion?.kind !== "settingsContinuation") return flow;
    const continued = await readFlow(
      "settings",
      flow.completion.flowId,
      `${label} continuation`,
      signal,
    );
    if (continued.completion !== null) {
      throw new KratosBrowserClientError(
        "Identity settings continuation did not return an editable flow.",
        { kind: "contract" },
      );
    }
    const messages = [...flow.messages, ...continued.messages];
    if (messages.length > 16) {
      throw new KratosBrowserClientError(
        "Identity settings continuation returned too many messages.",
        { kind: "contract" },
      );
    }
    return Object.freeze({
      ...continued,
      messages: Object.freeze(messages),
    });
  }

  async function renewSession({ signal } = {}) {
    const url = `${context.identityBaseUrl}/session-renewal/v1`;
    const authorization = await fetchJson(url, { signal });
    if (authorization.status === 204) return;
    if (authorization.status !== 200) {
      throw new KratosBrowserClientError("Session renewal authorization failed.", { status: authorization.status });
    }
    const decoded = decodeAccountMutationAuthorizationV1(
      await readBoundedAccountJson(authorization, "Session renewal authorization", 256),
    );
    if (decoded.kind !== "cookieCsrf") {
      throw new KratosBrowserClientError("Session renewal requires cookie authorization.", { kind: "contract" });
    }
    const response = await fetchJson(url, {
      method: "POST", signal, headers: { "x-fishystuff-csrf": decoded.token },
    });
    if (response.status !== 204) {
      throw new KratosBrowserClientError("Session renewal failed.", { status: response.status });
    }
  }

  async function session({ signal } = {}) {
    const response = await fetchJson(context.sessionUrl, { signal });
    if (response.status === 401) return Object.freeze({ kind: "signedOut" });
    if (response.status === 403) {
      return Object.freeze({
        kind: "reauthenticationRequired",
        ...decodeSessionError(
          await readBoundedJson(response, "Identity session"),
          context,
        ),
      });
    }
    if (!response.ok) {
      throw new KratosBrowserClientError(
        `Identity session returned HTTP ${response.status}.`,
        { status: response.status },
      );
    }
    return Object.freeze({
      kind: "ready",
      session: decodeActiveSession(
        await readBoundedJson(response, "Identity session"),
      ),
    });
  }

  async function startFlow(view, { mode = "default", signal } = {}) {
    const response = await fetchJson(startUrl(context, view, mode), { signal });
    if (view === "login" && mode === "default" && response.status === 400) {
      return decodeSessionAlreadyAvailable(await readBoundedJson(response, "Identity login"));
    }
    if (response.status === 401) return Object.freeze({ kind: "signedOut" });
    if (response.status === 403) {
      return Object.freeze({
        kind: "reauthenticationRequired",
        ...decodeSessionError(
          await readBoundedJson(response, `Identity ${view}`),
          context,
        ),
      });
    }
    if (view === "registration" && response.status === 400) {
      throw new KratosBrowserClientError(
        "New account registration is currently unavailable.",
        { kind: "disabled", status: response.status },
      );
    }
    if (!response.ok) {
      throw new KratosBrowserClientError(
        `Identity ${view} initialization returned HTTP ${response.status}.`,
        { status: response.status },
      );
    }
    const flow = decodeFlow(
      await readBoundedJson(response, `Identity ${view}`),
      context,
      view,
    );
    return Object.freeze({
      kind: "flow",
      flow: await resolveFlow(flow, `Identity ${view}`, signal),
    });
  }

  async function loadFlow(view, flowId, { signal } = {}) {
    if (!FLOW_VIEWS.has(view) || typeof flowId !== "string") {
      throw new TypeError("Identity flow continuation is invalid.");
    }
    return Object.freeze({
      kind: "flow",
      flow: await resolveFlow(
        await readFlow(view, flowId, `Identity ${view}`, signal),
        `Identity ${view}`,
        signal,
      ),
    });
  }

  async function startProvider(
    flow,
    submission,
    { signal, reauthenticationContext = context } = {},
  ) {
    if (
      !isRecord(flow)
      || !["login", "registration", "settings"].includes(flow.view)
      || typeof flow.action !== "string"
    ) {
      throw new TypeError("Identity provider flow is invalid.");
    }
    const body = await submissionBody(flow, submission, signal);
    const response = await fetchJson(flow.action, {
      method: "POST",
      body,
      signal,
    });
    if (response.status === 403) {
      return Object.freeze({
        kind: "reauthenticationRequired",
        ...decodeSessionError(
          await readBoundedJson(response, `Identity ${flow.view}`),
          reauthenticationContext,
          { refreshReturnUrl: flow.action },
        ),
      });
    }
    if (response.status !== 422) {
      throw new KratosBrowserClientError(
        `Discord continuation returned HTTP ${response.status}.`,
        { status: response.status },
      );
    }
    return decodeDiscordLocationChange(
      await readBoundedJson(response, "Discord continuation"),
      context,
    );
  }

  async function submitFlow(flow, submission, { signal } = {}) {
    if (!flow || !FLOW_VIEWS.has(flow.view) || typeof flow.action !== "string") {
      throw new TypeError("Identity flow submission is invalid.");
    }
    const body = await submissionBody(flow, submission, signal);
    const response = await fetchJson(flow.action, {
      method: "POST",
      body,
      signal,
    });
    if (response.status === 422) {
      const continuation = decodeBrowserLocationChange(
        await readBoundedJson(response, `Identity ${flow.view} continuation`),
        context,
      );
      if (continuation.kind !== "flowContinuation") return continuation;
      const continued = await readFlow(
        continuation.view,
        continuation.flowId,
        `Identity ${continuation.view} continuation`,
        signal,
      );
      return Object.freeze({
        kind: "flow",
        flow: await resolveFlow(
          continued,
          `Identity ${continuation.view} continuation`,
          signal,
        ),
      });
    }
    if ([404, 410].includes(response.status)) {
      return Object.freeze({ kind: "expired" });
    }
    if (response.status === 401) return Object.freeze({ kind: "signedOut" });
    if (response.status === 403) {
      return Object.freeze({
        kind: "reauthenticationRequired",
        ...decodeSessionError(
          await readBoundedJson(response, `Identity ${flow.view}`),
          context,
          { refreshReturnUrl: flow.action },
        ),
      });
    }
    if (![200, 400].includes(response.status)) {
      throw new KratosBrowserClientError(
        `Identity ${flow.view} submission returned HTTP ${response.status}.`,
        { status: response.status },
      );
    }
    const value = await readBoundedJson(response, `Identity ${flow.view}`);
    if (
      response.status === 200
      && ["login", "registration"].includes(flow.view)
      && value?.session
    ) {
      return Object.freeze({
        kind: "authenticated",
        session: decodeSuccessfulSession(value),
      });
    }
    const decoded = decodeFlow(value, context, flow.view);
    return Object.freeze({
      kind: response.status === 400 ? "validation" : "flow",
      flow: await resolveFlow(decoded, `Identity ${flow.view}`, signal),
    });
  }

  async function createLogout({ signal } = {}) {
    const response = await fetchJson(context.logoutFlowUrl, { signal });
    if (response.status === 401) return Object.freeze({ kind: "signedOut" });
    if (!response.ok) {
      throw new KratosBrowserClientError(
        `Logout flow returned HTTP ${response.status}.`,
        { status: response.status },
      );
    }
    return Object.freeze({
      kind: "ready",
      ...decodeLogoutFlow(
        await readBoundedJson(response, "Logout flow"),
        context,
      ),
    });
  }

  async function logout(logoutUrl, { signal } = {}) {
    const decoded = decodeLogoutFlow({ logout_url: logoutUrl }, context);
    const response = await fetchJson(decoded.logoutUrl, { signal });
    if (response.status !== 204) {
      throw new KratosBrowserClientError(
        `Logout returned HTTP ${response.status}.`,
        { status: response.status },
      );
    }
    return Object.freeze({ kind: "signedOut" });
  }

  return Object.freeze({
    createLogout,
    loadFlow,
    logout,
    renewSession,
    session,
    startFlow,
    startProvider,
    submitFlow,
  });
}

export function isIdentityBrowserContractFailure(error) {
  return error instanceof IdentityBrowserContractError
    || error instanceof KratosBrowserClientError;
}
